import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { PreconPhotoThumbButton } from "./precon-photo-lightbox";
import { PreconPlanWithPins } from "./precon-plan-with-pins";
import {
  isEstablishingShot,
} from "./derive-precon-sections";
import type {
  DerivedPhotoEntry,
  DerivedSection,
  PreconPlanImage,
} from "./derive-precon-sections";
import type { PreconSectionMeta } from "./precon-types";

interface Props {
  projectId: string;
  section: DerivedSection;
  meta: PreconSectionMeta;
  onMetaChange: (next: PreconSectionMeta) => void;
  /** Per-photo caption overrides, keyed by `image_id`. */
  captions: Record<string, string>;
  onCaptionChange: (imageId: string, value: string) => void;
  /**
   * Plan image for the sheet's right column. Null mirrors the PDF's own
   * degradation: metadata + notes render without the plan, photos keep
   * their numbers.
   */
  planImage: PreconPlanImage | null;
  /** Document-wide U-numbers for not-located records, keyed by record id. */
  uNumbers: ReadonlyMap<string, number>;
  /** Opens the tap-to-enlarge lightbox for a photo with its document label. */
  onPhotoOpen: (photo: DerivedPhotoEntry, label: string) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}

/** One labelled row of the print-mirroring metadata table. */
function MetaRow({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <tr className="border-b last:border-0">
      <th scope="row" className="w-28 py-2 pr-3 text-left align-middle">
        <Label
          htmlFor={htmlFor}
          className="text-xs font-medium text-muted-foreground"
        >
          {label}
        </Label>
      </th>
      <td className="py-1.5">{children}</td>
    </tr>
  );
}

export function PreconSectionCard({
  projectId,
  section,
  meta,
  onMetaChange,
  captions,
  onCaptionChange,
  planImage,
  uNumbers,
  onPhotoOpen,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: Props) {
  const set = (patch: Partial<PreconSectionMeta>) =>
    onMetaChange({ ...meta, ...patch });
  const setNote = (i: number, value: string) =>
    set({ notes: meta.notes.map((n, j) => (j === i ? value : n)) });

  const unpinnedCount = section.unpinned.length;

  return (
    <Card>
      <CardHeader className="grid-cols-[1fr_auto] items-center">
        <div>
          <CardTitle>{section.planName}</CardTitle>
        </div>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Move section up"
            disabled={isFirst}
            onClick={onMoveUp}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Move section down"
            disabled={isLast}
            onClick={onMoveDown}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Section header sheet, as printed: metadata table + NOTE list on
            the left, the plan image with numbered pins on the right. The
            plan is first in DOM so phones stack it on top (spec Layout §
            Mobile); md+ orders it to the right column. */}
        <div className={cn("grid gap-5", planImage && "md:grid-cols-2")}>
          {planImage && (
            <div className="md:order-2">
              <PreconPlanWithPins
                planImage={planImage}
                located={section.located}
                planName={section.planName}
              />
            </div>
          )}

          <div className="space-y-5 md:order-1">
            {/* Survey metadata — the printed section header's table. */}
            <table className="w-full border-collapse text-sm">
              <tbody>
                <MetaRow
                  label="Pre-con date"
                  htmlFor={`precon-date-${section.plan_id}`}
                >
                  <Input
                    id={`precon-date-${section.plan_id}`}
                    type="date"
                    value={meta.precon_date}
                    onChange={(e) => set({ precon_date: e.target.value })}
                    className="h-8 text-sm"
                  />
                </MetaRow>
                <MetaRow label="Level" htmlFor={`level-${section.plan_id}`}>
                  <Input
                    id={`level-${section.plan_id}`}
                    value={meta.level}
                    onChange={(e) => set({ level: e.target.value })}
                    className="h-8 text-sm"
                  />
                </MetaRow>
                <MetaRow label="Grid lines" htmlFor={`grid-${section.plan_id}`}>
                  <Input
                    id={`grid-${section.plan_id}`}
                    value={meta.grid_lines}
                    onChange={(e) => set({ grid_lines: e.target.value })}
                    className="h-8 text-sm"
                  />
                </MetaRow>
                <MetaRow
                  label="Location"
                  htmlFor={`location-${section.plan_id}`}
                >
                  <Input
                    id={`location-${section.plan_id}`}
                    value={meta.location}
                    onChange={(e) => set({ location: e.target.value })}
                    className="h-8 text-sm"
                  />
                </MetaRow>
              </tbody>
            </table>

            {/* Numbered NOTE list, as printed. */}
            <div className="space-y-2">
              <Label>Notes</Label>
              {meta.notes.map((note, i) => (
                <div key={i} className="flex items-end gap-2">
                  <div className="flex-1 space-y-1">
                    <Label
                      htmlFor={`note-${section.plan_id}-${i}`}
                      className="text-xs"
                    >
                      Note {i + 1}
                    </Label>
                    <Input
                      id={`note-${section.plan_id}-${i}`}
                      value={note}
                      onChange={(e) => setNote(i, e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove note"
                    onClick={() =>
                      set({ notes: meta.notes.filter((_, j) => j !== i) })
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => set({ notes: [...meta.notes, ""] })}
              >
                <Plus className="mr-1 h-4 w-4" /> Add note
              </Button>
            </div>
          </div>
        </div>

        {/* Located photos — numbered to match the plan pin */}
        <div className="space-y-2">
          <Label>Located photos</Label>
          {section.located.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No pinned photos on this plan.
            </p>
          ) : (
            <ul className="space-y-2">
              {section.located.map((entry) => {
                // The record's pin number, sub-lettered per photo (Na, Nb…);
                // a single-photo record reads the bare number.
                const ref = `${entry.recordNumber}${entry.subLetter ?? ""}`;
                const label = `(${ref})`;
                // The establishing shot seeds from the record remark; the rest
                // start blank. Caption state is keyed by image_id, so edits
                // never bleed across a record's photos.
                return (
                  <li key={entry.image_id} className="flex items-center gap-3">
                    <span className="w-8 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                      {label}
                    </span>
                    <PreconPhotoThumbButton
                      photo={entry}
                      label={label}
                      onOpen={onPhotoOpen}
                    />
                    <Input
                      aria-label={`Caption for photo ${ref}`}
                      value={captions[entry.image_id] ?? (isEstablishingShot(entry) ? entry.remark : "")}
                      onChange={(e) =>
                        onCaptionChange(entry.image_id, e.target.value)
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Plan-but-unpinned photos — shown, demarcated, with a pinning nudge.
            Each carries its (U#) — the document's continuous not-located
            sequence, never the pinned (n) form. */}
        {unpinnedCount > 0 && (
          <div className="space-y-2 rounded-md border border-dashed p-3">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">Not located on this plan</Badge>
              <span className="text-xs text-muted-foreground">
                {/* In-app navigation (not a new tab): the hand-off selection
                    lives in sessionStorage, so the builder resumes on return
                    with the new pins patched in (US-003). */}
                <Link
                  to="/projects/$projectId/plans"
                  params={{ projectId }}
                  search={{ plan: section.plan_id }}
                  className="text-primary underline-offset-4 hover:underline"
                >
                  Pin them on this plan
                </Link>{" "}
                for a numbered reference.
              </span>
            </div>
            <ul className="space-y-2">
              {section.unpinned.map((entry) => {
                // One U# per record (shared by its photos), sub-lettered per
                // photo: (U3a), (U3b)…; a single-photo record reads (U3).
                const uNum = uNumbers.get(entry.record_id) ?? "?";
                const label = `(U${uNum}${entry.subLetter ?? ""})`;
                // Establishing shot seeds from the record remark; the rest
                // start blank. Caption state is keyed by image_id.
                const captionValue =
                  captions[entry.image_id] ??
                  (isEstablishingShot(entry) ? entry.remark : "");
                return (
                  <li key={entry.image_id} className="flex items-center gap-3">
                    <span className="w-10 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                      {label}
                    </span>
                    <PreconPhotoThumbButton
                      photo={entry}
                      label={label}
                      onOpen={onPhotoOpen}
                    />
                    <Input
                      aria-label={`Caption for photo ${label}`}
                      value={captionValue}
                      onChange={(e) =>
                        onCaptionChange(entry.image_id, e.target.value)
                      }
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
